/**
 * MobileControls - Controles de velocidad optimizados para móvil (overlay inferior con
 * slider y gestos táctiles), más la detección de dispositivo móvil.
 *
 * Extraído de UIController para reducir su tamaño. Necesita una referencia a UIController
 * (`ui`) porque la UI móvil controla la simulación a través de él (setSpeed, adjustSpeed,
 * togglePlayPause) y lee su estado (speedPresets, currentSpeed, isPaused).
 */
export class MobileControls {
    /**
     * @param {Object} ui - La instancia de UIController que orquesta la simulación
     */
    constructor(ui) {
        this.ui = ui;
    }

    /**
     * Detect if the device is mobile based on screen width
     * @returns {boolean} True if mobile device
     */
    detectMobile() {
        return window.innerWidth <= 768;
    }

    /**
     * Create mobile-optimized speed controls
     */
    createMobileSpeedControls() {
        // Create mobile speed control overlay
        const mobileControls = document.createElement('div');
        mobileControls.id = 'mobile-speed-controls';
        mobileControls.innerHTML = `
            <div class="mobile-speed-container">
                <div class="mobile-speed-header">
                    <span class="mobile-speed-label">Velocidad</span>
                    <span id="mobile-speed-display" class="mobile-speed-value">Muy Lento (0.1 años/s)</span>
                </div>

                <div class="mobile-speed-slider-container">
                    <input type="range" id="mobile-speed-slider" min="0" max="6" step="1" value="1" class="mobile-speed-slider">
                </div>
            </div>
        `;

        // Add mobile-specific styles
        const mobileStyles = document.createElement('style');
        mobileStyles.textContent = `
            #mobile-speed-controls {
                position: fixed;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 2000;
                background: rgba(0, 0, 0, 0.8);
                border-radius: 3px;
                padding: 7px;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                min-width: 280px;
                max-width: 50vw;
            }

            .mobile-speed-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 12px;
            }

            .mobile-speed-header {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
            }

            .mobile-speed-label {
                color: #ffffff;
                font-size: 14px;
                font-weight: bold;
                opacity: 0.9;
            }

            .mobile-speed-value {
                color: #4FC3F7;
                font-size: 12px;
                font-weight: 500;
            }

            .mobile-speed-buttons {
                display: flex;
                gap: 15px;
                align-items: center;
            }

            .mobile-speed-btn {
                width: 50px;
                height: 50px;
                border: none;
                border-radius: 50%;
                background: linear-gradient(145deg, #2196F3, #1976D2);
                color: white;
                font-size: 20px;
                font-weight: bold;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                user-select: none;
                -webkit-tap-highlight-color: transparent;
            }

            .mobile-speed-btn:active {
                transform: scale(0.95);
                box-shadow: 0 1px 4px rgba(33, 150, 243, 0.5);
            }

            .mobile-speed-btn.play-pause {
                width: 60px;
                height: 60px;
                background: linear-gradient(145deg, #4CAF50, #388E3C);
                box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
            }

            .mobile-speed-btn.play-pause.paused {
                background: linear-gradient(145deg, #FF9800, #F57C00);
                box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
            }

            .mobile-speed-btn.speed-down {
                background: linear-gradient(145deg, #FF5722, #D84315);
                box-shadow: 0 2px 8px rgba(255, 87, 34, 0.3);
            }

            .mobile-speed-btn.speed-up {
                background: linear-gradient(145deg, #9C27B0, #7B1FA2);
                box-shadow: 0 2px 8px rgba(156, 39, 176, 0.3);
            }

            .mobile-btn-icon {
                font-size: 24px;
                line-height: 1;
            }

            .mobile-speed-slider-container {
                width: 100%;
                padding: 0 10px;
            }

            .mobile-speed-slider {
                width: 100%;
                height: 8px;
                border-radius: 4px;
                background: rgba(255, 255, 255, 0.2);
                outline: none;
                -webkit-appearance: none;
                appearance: none;
            }

            .mobile-speed-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: linear-gradient(145deg, #4FC3F7, #0288D1);
                cursor: pointer;
                box-shadow: 0 2px 6px rgba(79, 195, 247, 0.4);
            }

            .mobile-speed-slider::-moz-range-thumb {
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: linear-gradient(145deg, #4FC3F7, #0288D1);
                cursor: pointer;
                border: none;
                box-shadow: 0 2px 6px rgba(79, 195, 247, 0.4);
            }

            /* Hide mobile controls on desktop */
            @media (min-width: 769px) {
                #mobile-speed-controls {
                    display: none !important;
                }
            }

            /* Responsive adjustments */
            @media (max-width: 480px) {
                #mobile-speed-controls {
                    bottom: 20px;
                    left: 20px;
                    right: 10px;
                    transform: none;
                    min-width: auto;
                }

                .mobile-speed-btn {
                    width: 45px;
                    height: 45px;
                }

                .mobile-speed-btn.play-pause {
                    width: 55px;
                    height: 55px;
                }
            }
        `;

        document.head.appendChild(mobileStyles);
        document.body.appendChild(mobileControls);

        // Setup mobile event listeners
        this.setupMobileEventListeners();
    }

    /**
     * Remove mobile speed controls
     */
    removeMobileSpeedControls() {
        const mobileControls = document.getElementById('mobile-speed-controls');
        if (mobileControls) {
            mobileControls.remove();
        }
    }

    /**
     * Setup event listeners for mobile controls
     */
    setupMobileEventListeners() {
        // Mobile speed buttons
        const mobileSpeedDown = document.getElementById('mobile-speed-down');
        const mobileSpeedUp = document.getElementById('mobile-speed-up');
        const mobilePlayPause = document.getElementById('mobile-play-pause');
        const mobileSpeedSlider = document.getElementById('mobile-speed-slider');

        mobileSpeedDown?.addEventListener('click', () => this.ui.adjustSpeed(-1));
        mobileSpeedUp?.addEventListener('click', () => this.ui.adjustSpeed(1));
        mobilePlayPause?.addEventListener('click', () => this.ui.togglePlayPause());
        mobileSpeedSlider?.addEventListener('input', (e) =>
            this.ui.setSpeed(parseInt(e.target.value))
        );

        // Touch gestures for speed control
        this.setupMobileGestures();
    }

    /**
     * Setup mobile gesture controls
     */
    setupMobileGestures() {
        const mobileControls = document.getElementById('mobile-speed-controls');
        if (!mobileControls) return;

        let startY = 0;
        let startSpeed = 0;

        mobileControls.addEventListener(
            'touchstart',
            (e) => {
                startY = e.touches[0].clientY;
                startSpeed = this.getCurrentSpeedIndex();
            },
            { passive: true }
        );

        mobileControls.addEventListener(
            'touchmove',
            (e) => {
                e.preventDefault();
                const currentY = e.touches[0].clientY;
                const deltaY = startY - currentY;
                const sensitivity = 30; // pixels per speed level

                const speedChange = Math.floor(deltaY / sensitivity);
                const newSpeedIndex = Math.max(
                    0,
                    Math.min(this.ui.speedPresets.length - 1, startSpeed + speedChange)
                );

                if (newSpeedIndex !== this.getCurrentSpeedIndex()) {
                    this.ui.setSpeed(newSpeedIndex);
                    this.updateMobileSpeedDisplay();
                }
            },
            { passive: false }
        );
    }

    /**
     * Get current speed index
     * @returns {number} Current speed preset index
     */
    getCurrentSpeedIndex() {
        return this.ui.speedPresets.findIndex((preset) => preset.value === this.ui.currentSpeed);
    }

    /**
     * Update mobile speed display
     */
    updateMobileSpeedDisplay() {
        const mobileSpeedDisplay = document.getElementById('mobile-speed-display');
        const mobileSpeedSlider = document.getElementById('mobile-speed-slider');
        const mobilePlayPause = document.getElementById('mobile-play-pause');

        if (mobileSpeedDisplay) {
            const currentIndex = this.getCurrentSpeedIndex();
            const preset = this.ui.speedPresets[currentIndex];
            if (preset) {
                const speedText =
                    preset.value === 0
                        ? `${preset.label} (0 años/s)`
                        : `${preset.label} (${preset.value} año${preset.value === 1 ? '' : 's'}/s)`;
                mobileSpeedDisplay.textContent = speedText;
            }
        }

        if (mobileSpeedSlider) {
            mobileSpeedSlider.value = this.getCurrentSpeedIndex().toString();
        }

        if (mobilePlayPause) {
            const icon = mobilePlayPause.querySelector('.mobile-btn-icon');
            if (this.ui.isPaused) {
                icon.textContent = '▶️';
                mobilePlayPause.classList.add('paused');
            } else {
                icon.textContent = '⏸️';
                mobilePlayPause.classList.remove('paused');
            }
        }
    }
}
